/** @jsxImportSource @emotion/react */
// import DateTimePicker from 'components/atoms/DateTimePicker'
import InputMask from "components/atoms/InputMask";
import SearchableInput from 'components/atoms/SearchableInput'
import { Option, SelectInput } from 'components/atoms/Select'
import { Field, FieldArray, FormikErrors, FormikTouched } from 'formik'
import moment from "moment/moment";
import { formatCalendarDate } from 'helpers/dates'
import React from 'react'
import Select from 'react-select'
import { diagnosisCodes } from 'ts-common/constants/diagnosisCodes'
import {
  BillingChargeFormValues,
  ChargeSection,
} from 'components/lab/billingCharge/BillingChargeUpsert'
import { BillingChargeProcedure } from 'store/types'

// A date of service is stored at UTC noon of the typed day, as the backend
// stores generated ones: the same day in every zone, whoever typed it.
const toDosIso = (day: moment.Moment): string => day.clone().utc().hour(12).toISOString()

interface ChargeProcedureProps {
  idx: number
  proc: BillingChargeProcedure
  procedureOptions: Option[] | undefined
  ensureTrailingBlank: () => void
  errors: FormikErrors<BillingChargeFormValues>
  touched: FormikTouched<BillingChargeFormValues>
  setFieldValue: (field: string, value: any) => void
  submitCount: number
  disabled?: boolean
  isPatientCharge?: boolean
  disablePlanAmount?: boolean
}

export const ChargeProcedureRow = ({
  idx,
  proc,
  procedureOptions,
  ensureTrailingBlank,
  setFieldValue,
  errors,
  submitCount,
  touched,
  disabled = false,
  isPatientCharge = false,
  disablePlanAmount = false,
}: ChargeProcedureProps) => {
  const [endDosInput, setEndDosInput] = React.useState(
    formatCalendarDate(proc.endDos, '')
  )
  const [startDosInput, setStartDosInput] = React.useState(
    formatCalendarDate(proc.startDos, '')
  )

  React.useEffect(() => {
    setStartDosInput(formatCalendarDate(proc.startDos, ''))
    setEndDosInput(formatCalendarDate(proc.endDos, ''))
  }, [proc.startDos, proc.endDos])

  return (
    <div className="row mb-2" key={idx}>
    <div className="col">
      <SearchableInput
        className="basic-single"
        value={proc.cptCode}
        options={procedureOptions || []}
        onBlur={ensureTrailingBlank}
        onChange={(value) => {
          setFieldValue(`procedures[${idx}].cptCode`, value)
          ensureTrailingBlank()
        }}
        placeholder="Enter CPT code"
        disabled={disabled}
      />
    </div>
    <div className="col">
      <InputMask
        mask="99/99/9999"
        className="form-control"
        placeholder="MM/DD/YYYY"
        value={startDosInput}
        disabled={disabled}
        onChange={(e) => {
          setStartDosInput(e.target.value)
        }}
        onBlur={() => {
          const raw = startDosInput.trim()
          if (!raw) {
            setFieldValue(`procedures[${idx}].startDos`, '')
            setFieldValue(`procedures[${idx}].endDos`, '')
            setEndDosInput('')
            ensureTrailingBlank()
            return
          }

          const parsed = moment.utc(raw, 'MM/DD/YYYY', true)
          if (parsed.isValid()) {
            const iso = toDosIso(parsed)
            const formatted = parsed.format('MM/DD/YYYY')

            // auto-sync end DoS only if it's empty
            if (!endDosInput.trim()) {
              setFieldValue(`procedures[${idx}].endDos`, iso)
              setEndDosInput(formatted)
            }

            setFieldValue(`procedures[${idx}].startDos`, iso)
          } else {
            setFieldValue(`procedures[${idx}].startDos`, '')
          }

          ensureTrailingBlank()
        }}
      />
    </div>
    <div className="col">
      <InputMask
        mask="99/99/9999"
        className="form-control"
        placeholder="MM/DD/YYYY"
        value={endDosInput}
        disabled={disabled}
        onChange={(e) => {
          setEndDosInput(e.target.value)
        }}
        onBlur={() => {
          const raw = endDosInput.trim()
          if (!raw) {
            setFieldValue(`procedures[${idx}].endDos`, '')
            ensureTrailingBlank()
            return
          }

          const parsed = moment.utc(raw, 'MM/DD/YYYY', true)
          if (parsed.isValid()) {
            setFieldValue(`procedures[${idx}].endDos`, toDosIso(parsed))
          } else {
            setFieldValue(`procedures[${idx}].endDos`, '')
          }
          ensureTrailingBlank()
        }}
      />
    </div>
    <div className="col">
      <Field
        type="text"
        name={`procedures[${idx}].modifier`}
        className="form-control"
        onKeyUp={() => ensureTrailingBlank()}
        disabled={disabled}
      />
    </div>
    <div className="col">
      <Field
        type="text"
        name={`procedures[${idx}].dxPointers`}
        className="form-control"
        placeholder="Dx. Ptr *"
        onKeyUp={() => ensureTrailingBlank()}
        disabled={disabled}
      />
      {((touched.procedures?.[idx] as any)?.dxPointers || submitCount > 0) &&
        (errors.procedures?.[idx] as any)?.dxPointers && (
          <div className="text-danger">
            {(errors.procedures?.[idx] as any)?.dxPointers as string}
          </div>
        )}
    </div>
    <div className="col d-flex">
      <Field
        type="number"
        name={`procedures[${idx}].unitsValue`}
        className="form-control"
        placeholder="Units"
        onKeyUp={() => ensureTrailingBlank()}
        disabled={disabled}
      />
      <Field
        component={SelectInput}
        options={[
          { key: 'UN', value: 'UN' },
          { key: 'MJ', value: 'MJ' },
        ]}
        wrapperClassName="mb-0 pb-2"
        name={`procedures[${idx}].units`}
        css={{ width: 50, marginLeft: 10, height: '100%' }}
        disabled={disabled}
      />
    </div>
    <div className="col">
      <Field
        type="number"
        name={`procedures[${idx}].patientAmount`}
        className="form-control"
        placeholder="Patient $"
        onKeyUp={() => ensureTrailingBlank()}
        disabled={disabled}
      />
    </div>
    <div className="col">
      <Field
        type="number"
        name={`procedures[${idx}].planAmount`}
        className="form-control"
        placeholder="Plan $"
        onKeyUp={() => ensureTrailingBlank()}
        disabled={disabled || isPatientCharge || disablePlanAmount}
      />
    </div>
  </div>
)}

interface SectionProps {
  values: BillingChargeFormValues
  setFieldValue: (field: string, value: any) => void
  errors: FormikErrors<BillingChargeFormValues>
  touched: FormikTouched<BillingChargeFormValues>
  submitCount: number
  blankProcedure: BillingChargeProcedure
  procedureOptions: Option[] | undefined
  disabled?: boolean
  isPatientCharge?: boolean
  disablePlanAmount?: boolean
  chargeSummary?: {
    appliedPatientAmount?: number
    appliedPlanAmount?: number
    currentPatientBalance?: number
    currentPlanBalance?: number
    writeOffAmount?: number
  }
}

export const ChargeProceduresSection = ({
  values,
  errors,
  touched,
  blankProcedure,
  submitCount,
  setFieldValue,
  procedureOptions,
  disabled = false,
  isPatientCharge = false,
  disablePlanAmount = false,
  chargeSummary,
}: SectionProps) => {
  const chargedPatientTotal =
    (
      values?.procedures?.reduce((acc, proc) => {
        return acc + (proc?.patientAmount || 0)
      }, 0) || 0
    )?.toFixed(2)

  const chargedPlanTotal =
    (
      values?.procedures?.reduce((acc, proc) => {
        return acc + (proc?.planAmount || 0)
      }, 0) || 0
    )?.toFixed(2)

  const chargedTotal =
    (
      values?.procedures?.reduce((acc, proc) => {
        return acc + (proc?.planAmount || 0) + (proc?.patientAmount || 0)
      }, 0) || 0
    )?.toFixed(2)

  const appliedPatientTotal = Number(chargeSummary?.appliedPatientAmount || 0).toFixed(2)
  const appliedPlanTotal = Number(chargeSummary?.appliedPlanAmount || 0).toFixed(2)
  const appliedTotal = Number(
    Number(chargeSummary?.appliedPatientAmount || 0) + Number(chargeSummary?.appliedPlanAmount || 0),
  ).toFixed(2)

  const remainingPatientTotal = Number(chargeSummary?.currentPatientBalance || 0).toFixed(2)
  const remainingPlanTotal = Number(chargeSummary?.currentPlanBalance || 0).toFixed(2)
  const remainingTotal = Number(
    Number(chargeSummary?.currentPatientBalance || 0) + Number(chargeSummary?.currentPlanBalance || 0),
  ).toFixed(2)

  const writeOffTotal = Number(chargeSummary?.writeOffAmount || 0).toFixed(2)

  return (
    <ChargeSection title={'Diagnosis & Procedures'}>
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col">
            <label htmlFor="diagnosis">Diagnosis 1</label>
            <Select
              className="basic-single w-100 mr-3"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 100 }),
              }}
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Select Diagnosis Code"
              name={`diagnosisCodes[0]`}
              onChange={(item) => setFieldValue(`diagnosisCodes[0]`, item?.value ?? '')}
              value={diagnosisCodes.find((d) => d.value === values.diagnosisCodes[0]) || null}
              isDisabled={disabled}
              options={diagnosisCodes}
            />
          </div>
          <div className="col">
            <label htmlFor="diagnosis">Diagnosis 2</label>
            <Select
              className="basic-single w-100 mr-3"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 100 }),
              }}
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Select Diagnosis Code"
              name={`diagnosisCodes[1]`}
              onChange={(item) => setFieldValue(`diagnosisCodes[1]`, item?.value ?? '')}
              value={diagnosisCodes.find((d) => d.value === values.diagnosisCodes[1]) || null}
              isDisabled={!values.diagnosisCodes[0] || disabled}
              options={diagnosisCodes}
            />
          </div>
          <div className="col">
            <label htmlFor="diagnosis">Diagnosis 3</label>
            <Select
              className="basic-single w-100 mr-3"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 100 }),
              }}
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Select Diagnosis Code"
              name={`diagnosisCodes[2]`}
              onChange={(item) => setFieldValue(`diagnosisCodes[2]`, item?.value ?? '')}
              value={diagnosisCodes.find((d) => d.value === values.diagnosisCodes[2]) || null}
              isDisabled={!values.diagnosisCodes[1] || disabled}
              options={diagnosisCodes}
            />
          </div>
          <div className="col">
            <label htmlFor="diagnosis">Diagnosis 4</label>
            <Select
              className="basic-single w-100 mr-3"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 100 }),
              }}
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Select Diagnosis Code"
              name={`diagnosisCodes[3]`}
              onChange={(item) => setFieldValue(`diagnosisCodes[3]`, item?.value ?? '')}
              value={diagnosisCodes.find((d) => d.value === values.diagnosisCodes[3]) || null}
              isDisabled={!values.diagnosisCodes[2] || disabled}
              options={diagnosisCodes}
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <label htmlFor="procedures">
              Procedures <span className="text-danger">*</span>
            </label>
          </div>
          <div className="col">
            <label htmlFor="startDos">
              Start DoS <span className="text-danger">*</span>
            </label>
          </div>
          <div className="col">
            <label htmlFor="endDos">
              End DoS <span className="text-danger">*</span>
            </label>
          </div>
          <div className="col">
            <label htmlFor="modifier">Modifier</label>
          </div>
          <div className="col">
            <label htmlFor="dxPointers">
              Dx. Ptr <span className="text-danger">*</span>
            </label>
          </div>
          <div className="col">
            <label htmlFor="units">
              Units <span className="text-danger">*</span>
            </label>
          </div>
          <div className="col">
            <label htmlFor="patientAmount">
              Patient $ <span className="text-danger">*</span>
            </label>
          </div>
          <div className="col">
            <label htmlFor="planAmount">
              Plan $ <span className="text-danger">*</span>
            </label>
          </div>
        </div>
        <FieldArray name="procedures">
          {({ push, remove }) => (
            <>
              {values.procedures.map((proc, idx) => {
                const isEmpty =
                  !proc.cptCode &&
                  !proc.modifier &&
                  !proc.unitsValue &&
                  !proc.planAmount &&
                  !proc.patientAmount &&
                  !proc.dxPointers

                const ensureTrailingBlank = () => {
                  if (!isEmpty && idx === values.procedures.length - 1) {
                    push({ ...blankProcedure })
                  }
                  if (isEmpty && idx < values.procedures.length - 1) {
                    remove(idx)
                  }
                }

                return (
                  <ChargeProcedureRow
                    ensureTrailingBlank={ensureTrailingBlank}
                    idx={idx}
                    procedureOptions={procedureOptions}
                    errors={errors}
                    touched={touched}
                    proc={proc}
                    setFieldValue={setFieldValue}
                    submitCount={submitCount}
                    disabled={disabled}
                    isPatientCharge={isPatientCharge}
                    disablePlanAmount={disablePlanAmount}
                  />
                )
              })}
            </>
          )}
        </FieldArray>
        <div
          className="row"
          css={{
            '& label': {
              fontSize: 18,
            },
          }}>
          <div className="col"></div>
          <div className="col"></div>
          <div className="col"></div>
          <div className="col"></div>
          <div className="col">
            <label htmlFor="units" className="font-weight-normal">
              Total
            </label>
          </div>
          <div className="col">
            <label htmlFor="totalAmount" className="font-weight-bold">
              $ {chargedTotal}
            </label>
          </div>
          <div className="col">
            <label htmlFor="patientAmount" className="font-weight-bold">
              $ {chargedPatientTotal}
            </label>
          </div>
          <div className="col">
            <label htmlFor="planAmount" className="font-weight-bold">
              $ {chargedPlanTotal}
            </label>
          </div>
        </div>
        {!!chargeSummary && (
          <>
            <div
              className="row"
              css={{
                '& label': {
                  fontSize: 16,
                  color: '#495057',
                },
              }}>
              <div className="col"></div>
              <div className="col"></div>
              <div className="col"></div>
              <div className="col"></div>
              <div className="col">
                <label htmlFor="appliedAmount" className="font-weight-normal">
                  Applied
                </label>
              </div>
              <div className="col">
                <label htmlFor="appliedTotal" className="font-weight-bold">
                  $ {appliedTotal}
                </label>
              </div>
              <div className="col">
                <label htmlFor="appliedPatient" className="font-weight-bold">
                  $ {appliedPatientTotal}
                </label>
              </div>
              <div className="col">
                <label htmlFor="appliedPlan" className="font-weight-bold">
                  $ {appliedPlanTotal}
                </label>
              </div>
            </div>
            <div
              className="row"
              css={{
                '& label': {
                  fontSize: 16,
                  color: '#212529',
                },
              }}>
              <div className="col"></div>
              <div className="col"></div>
              <div className="col"></div>
              <div className="col"></div>
              <div className="col">
                <label htmlFor="remainingAmount" className="font-weight-normal">
                  Remaining
                </label>
              </div>
              <div className="col">
                <label htmlFor="remainingTotal" className="font-weight-bold">
                  $ {remainingTotal}
                </label>
              </div>
              <div className="col">
                <label htmlFor="remainingPatient" className="font-weight-bold">
                  $ {remainingPatientTotal}
                </label>
              </div>
              <div className="col">
                <label htmlFor="remainingPlan" className="font-weight-bold">
                  $ {remainingPlanTotal}
                </label>
              </div>
            </div>
            <div
              className="row"
              css={{
                '& label': {
                  fontSize: 16,
                  color: '#212529',
                },
              }}>
              <div className="col"></div>
              <div className="col"></div>
              <div className="col"></div>
              <div className="col"></div>
              <div className="col">
                <label htmlFor="writeOffAmount" className="font-weight-normal">
                  Write-off
                </label>
              </div>
              <div className="col">
                <label htmlFor="writeOffTotal" className="font-weight-bold">
                  $ {writeOffTotal}
                </label>
              </div>
              <div className="col"></div>
              <div className="col">
                <label htmlFor="writeOffPlan" className="font-weight-bold">
                  $ {writeOffTotal}
                </label>
              </div>
            </div>
          </>
        )}
      </div>
    </ChargeSection>
  )
}
